import React, { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../../../../api/URL_API';
import { getPromoEspecial, claveGrupo, categoriasDelGrupo, LIMITE_UNIDADES_PROMO } from './promosEspeciales';
const CarritoContext = createContext();

export const useCarrito = () => useContext(CarritoContext);

export const CarritoProvider = ({ children }) => {
    const [carrito, setCarrito] = useState(() => {
        const savedCarrito = localStorage.getItem('carrito');
        return savedCarrito ? JSON.parse(savedCarrito) : [];
    });

    const [showAllProducts, setShowAllProducts] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [configEnvio, setConfigEnvio] = useState(null); // Configuración de envío
    const [reglasEnvio, setReglasEnvio] = useState([]); // Reglas de envío
    const [configDescuento, setConfigDescuento] = useState(null); // Configuración de descuentos
    const [reglasDescuento, setReglasDescuento] = useState([]); // Reglas de descuentos

    // Formateador para mostrar precios en pesos colombianos
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value);
    };

    useEffect(() => {
        localStorage.setItem('carrito', JSON.stringify(carrito));
    }, [carrito]);

    useEffect(() => {
        const fetchConfigAndRules = async () => {
            try {
                const configEnvioResponse = await fetch(`${API}/api/envios/configEnvio/2`);
                const configEnvioData = await configEnvioResponse.json();
                setConfigEnvio(configEnvioData);
    
                const reglasEnvioResponse = await fetch(`${API}/api/envios`);
                const reglasEnvioData = await reglasEnvioResponse.json();
                setReglasEnvio(reglasEnvioData);
    
                const configDescuentoResponse = await fetch(`${API}/api/descuentos/configDescuento/1`);
                const configDescuentoData = await configDescuentoResponse.json();
                setConfigDescuento(configDescuentoData);
    
                const reglasDescuentoResponse = await fetch(`${API}/api/descuentos`);
                const reglasDescuentoData = await reglasDescuentoResponse.json();
                setReglasDescuento(reglasDescuentoData);
            } catch (error) {
                console.error('Error al obtener configuración o reglas:', error);
            }
        };
    
        fetchConfigAndRules();
    }, []);

    const calculateShippingCost = () => {
        const totalCantidad = carrito.reduce((total, product) => total + (product.quantity || 1), 0);
        const totalPrecio = carrito.reduce((total, product) => {
            const price = parseFloat(product.precioU?.toString().replace('$', '').replace(',', '') || '0');
            return total + (price * (product.quantity || 1));
        }, 0);

        if (configEnvio?.tipoConfig === 'desable') {
            return 0;
        }

        let envioCosto = 0;

        if (configEnvio?.tipoConfig === 'cantidad') {
            const regla = reglasEnvio
                .filter(rule => rule.tipoRegla === 'cantidad')
                .sort((a, b) => b.max_cantidad - a.max_cantidad)
                .find(rule => totalCantidad >= (rule.min_cantidad || 0));
            envioCosto = regla?.costoEnvio || 0;
        } else if (configEnvio?.tipoConfig === 'total') {
            // Usamos el total con descuento para evaluar la regla de envío
            const totalConDescuento = calculateTotalWithDiscount();
            const regla = reglasEnvio
                .filter(rule => rule.tipoRegla === 'total')
                .sort((a, b) => b.max_total - a.max_total)
                .find(rule => totalConDescuento >= (rule.min_total || 0));
            envioCosto = regla?.costoEnvio || 0;
        }
        

        return envioCosto;
    };

    // Convierte un precio (string "$1,000" o número) a número.
    const parsePrice = (value) =>
        parseFloat((value === undefined || value === null ? '0' : value).toString().replace('$', '').replace(/,/g, '')) || 0;

    // Ley de aproximación: redondea al múltiplo de 100 más cercano (163.637 -> 163.600).
    const redondear100 = (value) => Math.round(value / 100) * 100;

    // -------------------------------------------------------------------------
    //  Motor central de totales.
    //  Los productos NORMALES y los de PROMOCIÓN se manejan de forma INDEPENDIENTE:
    //
    //  NORMALES:
    //    - Aplican el descuento global por cantidad/total (SOLO entre ellos).
    //    - Al "final de los descuentos" (ninguna regla aplica) pasan a mayorista.
    //
    //  PROMOCIÓN:
    //    - Precio por paquete (categorías del mismo grupo suman sus unidades).
    //    - Pasan a precio mayorista (y la promo deja de aplicar) cuando:
    //        a) se superan LIMITE_UNIDADES_PROMO unidades (combinadas), o
    //        b) los productos NORMALES ya están en mayorista.
    //      Es un acople de UN SOLO sentido: normal->promo SÍ, promo->normal NO
    //      (si la promo entra en mayorista, los normales siguen con su descuento).
    //
    //  Devuelve además:
    //    - detalle[]     : precio unitario efectivo y subtotal por línea (mismo
    //                      orden que `carrito`), para mostrarlo en el carrito.
    //    - avisosPromo[] : promociones a las que les faltan unidades para
    //                      completar el paquete (para avisar al usuario).
    // -------------------------------------------------------------------------
    const computeTotals = () => {
        const promoItems = carrito.filter(p => getPromoEspecial(p.nomCategoria));
        const normalItems = carrito.filter(p => !getPromoEspecial(p.nomCategoria));

        // ============ PRODUCTOS NORMALES ============
        // --- Descuento global: SOLO sobre productos sin promoción especial ---
        const totalCantidadNormal = normalItems.reduce((t, p) => t + (Number(p.quantity) || 1), 0);
        const totalPrecioNormal = normalItems.reduce((t, p) => t + parsePrice(p.precioU) * (Number(p.quantity) || 1), 0);

        // null = no aplica ninguna regla (precio mayorista); 0 = 0% (precio unitario).
        let descuento = null;
        if (configDescuento?.tipoConfig === 'desable') {
            descuento = 0;
        } else if (configDescuento?.tipoConfig === 'cantidad') {
            const regla = reglasDescuento
                .filter(rule => rule.tipoRegla === 'cantidad')
                .sort((a, b) => b.max_cantidad - a.max_cantidad)
                .find(rule =>
                    totalCantidadNormal >= (rule.min_cantidad || 0) &&
                    totalCantidadNormal <= (rule.max_cantidad || Infinity)
                );
            if (regla) descuento = regla.porcentajeDescuento;
        } else if (configDescuento?.tipoConfig === 'total') {
            const regla = reglasDescuento
                .filter(rule => rule.tipoRegla === 'total')
                .sort((a, b) => b.max_total - a.max_total)
                .find(rule =>
                    totalPrecioNormal >= (rule.min_total || 0) &&
                    totalPrecioNormal <= (rule.max_total || Infinity)
                );
            if (regla) descuento = regla.porcentajeDescuento;
        }

        // Mayorista para NORMALES: hay productos normales pero ninguna regla aplica.
        const normalMayorista =
            configDescuento?.tipoConfig !== 'desable' &&
            descuento === null &&
            normalItems.length > 0;

        // Base de referencia (precioU) y valor efectivo de los productos normales.
        const normalBase = normalItems.reduce(
            (t, p) => t + parsePrice(p.precioU) * (Number(p.quantity) || 1),
            0
        );
        const normalConDescuento = normalMayorista
            ? normalItems.reduce((t, p) => t + parsePrice(p.precioM) * (Number(p.quantity) || 1), 0)
            : normalBase - normalBase * ((descuento || 0) / 100);

        // ============ PRODUCTOS EN PROMOCIÓN ============
        // La promo pasa a mayorista si:
        //   a) supera su límite de unidades (combinadas), o
        //   b) los productos NORMALES ya están en mayorista (acople de un solo
        //      sentido: normal->promo SÍ, promo->normal NO).
        const totalPromoUnits = promoItems.reduce((t, p) => t + (Number(p.quantity) || 1), 0);
        const promoMayoristaPorLimite = totalPromoUnits > LIMITE_UNIDADES_PROMO;
        const promoMayorista = promoMayoristaPorLimite || normalMayorista;

        // Base de referencia (precioU) de las promos, para mostrar el ahorro.
        const promoBase = promoItems.reduce(
            (t, p) => t + parsePrice(p.precioU) * (Number(p.quantity) || 1),
            0
        );

        let promoTotal = 0;
        const avisosPromo = [];
        const grupos = {};

        if (promoMayorista) {
            // Se supera el límite: todas las promos a precio mayorista, sin promo.
            promoTotal = promoItems.reduce(
                (t, p) => t + parsePrice(p.precioM) * (Number(p.quantity) || 1),
                0
            );
        } else {
            // Se agrupa por GRUPO de promoción: categorías del mismo grupo suman sus
            // unidades juntas (1 monastery + 1 llamas = 2 unidades = 1 paquete).
            // Cada unidad entra con su precioU; se arman paquetes y las unidades
            // sueltas se cobran a precio unitario (dejando como sueltas las más
            // baratas: favorece al cliente).
            promoItems.forEach(p => {
                const key = claveGrupo(p.nomCategoria);
                if (!grupos[key]) {
                    grupos[key] = { key, promo: getPromoEspecial(p.nomCategoria), unidades: [] };
                }
                const qty = Number(p.quantity) || 1;
                for (let i = 0; i < qty; i++) grupos[key].unidades.push(parsePrice(p.precioU));
            });

            Object.values(grupos).forEach(grupo => {
                const { promo, unidades } = grupo;
                const cantidad = unidades.length;

                const paquetes = Math.floor(cantidad / promo.packSize);
                const sueltas = cantidad % promo.packSize;
                const ordenadas = [...unidades].sort((a, b) => a - b);
                const sueltasSum = ordenadas.slice(0, sueltas).reduce((a, b) => a + b, 0);
                const totalGrupo = paquetes * promo.packPrice + sueltasSum;

                grupo.totalGrupo = totalGrupo;
                // Precio unitario efectivo del grupo (promedio): 2 x 50.000 -> 25.000 c/u.
                grupo.unitPrice = cantidad > 0 ? totalGrupo / cantidad : 0;
                promoTotal += totalGrupo;

                // Aviso: faltan unidades para completar (otro) paquete de la promo.
                if (sueltas !== 0) {
                    avisosPromo.push({
                        categorias: categoriasDelGrupo(grupo.key),
                        faltan: promo.packSize - sueltas,
                        packSize: promo.packSize,
                        packPrice: promo.packPrice,
                    });
                }
            });
        }

        // ============ DETALLE POR LÍNEA (mismo orden que el carrito) ============
        const detalle = carrito.map(p => {
            const qty = Number(p.quantity) || 1;
            if (getPromoEspecial(p.nomCategoria)) {
                if (promoMayorista) {
                    const unit = parsePrice(p.precioM);
                    return { unit, subtotal: unit * qty, esPromo: true, mayorista: true };
                }
                const unit = grupos[claveGrupo(p.nomCategoria)].unitPrice;
                return { unit, subtotal: unit * qty, esPromo: true, mayorista: false };
            }
            const unit = normalMayorista ? parsePrice(p.precioM) : parsePrice(p.precioU);
            return { unit, subtotal: unit * qty, esPromo: false, mayorista: normalMayorista };
        });

        // ============ TOTALES Y AHORROS ============
        // Subtotal = precio de referencia efectivo (mayorista ya es el precio final).
        const subtotalBase =
            (normalMayorista ? normalConDescuento : normalBase) +
            (promoMayorista ? promoTotal : promoBase);

        const ahorroDescuento = normalMayorista ? 0 : (normalBase - normalConDescuento);
        const ahorroPromo = promoMayorista ? 0 : (promoBase - promoTotal);

        const totalFinal = redondear100(normalConDescuento + promoTotal);

        return {
            descuento,                                   // % del descuento global (o null)
            normalMayorista,
            promoMayorista,
            promoMayoristaPorLimite,
            subtotalBase,
            normalBase,
            normalConDescuento,
            ahorroDescuento,
            promoBase,
            promoTotal,
            ahorroPromo,
            totalFinal,                                  // total a pagar (sin envío), redondeado
            hayPromo: promoItems.length > 0,
            hayNormal: normalItems.length > 0,
            totalPromoUnits,
            avisosPromo,
            detalle,
        };
    };

    const calculateDiscount = () => computeTotals().descuento;

    const calculateTotalWithDiscount = () => computeTotals().totalFinal;

    // Resumen completo del carrito (para el detalle en el carrito y el pedido).
    const calculateCartSummary = () => computeTotals();



    const addToCarrito = (product) => {
        setCarrito((prevCarrito) => {
            const existingProductIndex = prevCarrito.findIndex(p => 
                p.idProducto === product.idProducto &&
                p.selectedColor === product.selectedColor &&
                p.selectedSize === product.selectedSize
            );
            console.log("stock",product.stock);

            const currentQuantity = existingProductIndex > -1 ? prevCarrito[existingProductIndex].quantity : 0;
            const newQuantity = currentQuantity + product.quantity;
    
            if (newQuantity > product.stock) {
                alert(`Solo puedes añadir ${product.stock} unidades de este producto`);
                console.log("stock",product.stock);

                return prevCarrito;
                
            }
            if (existingProductIndex > -1) {
                const updatedCarrito = [...prevCarrito];
                updatedCarrito[existingProductIndex].quantity += product.quantity;
                return updatedCarrito;
            } else {
                return [...prevCarrito, product];
            }
        });
    };
    

    const removeFromCarrito = (idProducto, selectedColor, selectedSize) => {
        setCarrito((prevCarrito) => 
            prevCarrito.filter(product => 
                product.idProducto !== idProducto ||
                product.selectedColor !== selectedColor ||
                product.selectedSize !== selectedSize
            )
        );
    };

    const increaseQuantity = (idProducto, selectedColor, selectedSize) => {
        setCarrito((prevCarrito) => 
            prevCarrito.map(product => {
                if (product.idProducto === idProducto &&
                    product.selectedColor === selectedColor &&
                    product.selectedSize === selectedSize) {
    
                    // Verificar si al aumentar supera el stock disponible
                    if (product.quantity + 1 > product.stock) {
                        alert(`No hay suficiente stock. Solo puedes agregar ${product.stock} unidades de este producto.`);
                        return product; // Retorna sin cambiar la cantidad
                    }
    
                    return { ...product, quantity: product.quantity + 1 };
                }
                return product;
            })
        );
    };
    

    const decreaseQuantity = (idProducto, selectedColor, selectedSize) => {
        setCarrito((prevCarrito) => 
            prevCarrito.map(product => 
                product.idProducto === idProducto &&
                product.selectedColor === selectedColor &&
                product.selectedSize === selectedSize &&
                product.quantity > 1
                    ? { ...product, quantity: product.quantity - 1 }
                    : product
            )
        );
    };

    const clearCarrito = () => {
        setCarrito([]);
        localStorage.removeItem('carrito');
    };

    const updateSelectedCategories = (categories) => {
        setSelectedCategories(categories);
    };

    return (
        <CarritoContext.Provider value={{
            carrito,
            addToCarrito,
            removeFromCarrito,
            increaseQuantity,
            decreaseQuantity,
            clearCarrito,
            showAllProducts,
            setShowAllProducts,
            selectedCategories,
            updateSelectedCategories,
            calculateShippingCost,
            calculateDiscount,
            calculateTotalWithDiscount,
            calculateCartSummary,
            formatCurrency,
            configEnvio,
            configDescuento
        }}>
            {children}
        </CarritoContext.Provider>
    );
};
